import { formatDateTime } from '@/shared/lib/format';
import type { GuestProfileResponse } from '@/shared/api';

export function GuestBillingBlock({ guest }: { guest: GuestProfileResponse }) {
  const b = guest.billing;
  const isB2B = guest.bookingType === 'B2B' && guest.company !== null;
  const hasAnyBilling =
    b.firstName || b.lastName || b.street || b.zip || b.city || b.country || b.email;
  if (!hasAnyBilling && !isB2B) {
    return (
      <>
        <p className="text-ink-soft border-hairline border-t pt-3 font-mono text-[10px] font-semibold uppercase tracking-[0.16em]">
          Rechnungsdaten
        </p>
        <p className="text-ink-soft text-sm">Noch nicht vom Gast eingetragen.</p>
      </>
    );
  }

  return (
    <>
      <p className="text-ink-soft border-hairline flex items-center gap-2 border-t pt-3 font-mono text-[10px] font-semibold uppercase tracking-[0.16em]">
        <span>Rechnungsdaten</span>
        <span className="rounded-pill bg-card-alt text-ink px-2 py-0.5 font-sans text-[11px] normal-case tracking-normal">
          {isB2B ? 'Firma' : 'Privat'}
        </span>
      </p>
      {isB2B && guest.company && (
        <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
          <Row label="Firmenname" value={guest.company.name} />
          <Row label="Straße" value={guest.company.street} colSpan={2} />
          <Row label="PLZ" value={guest.company.zip} />
          <Row label="Stadt" value={guest.company.city} />
          <Row label="Land" value={guest.company.country} />
          <Row
            label="Rechnung an"
            value={guest.company.invoiceEmail || `${guest.email ?? 'Gast-Adresse'} (Gast)`}
          />
          <Row label="USt-IdNr. / NIP / VAT ID" value={guest.company.vatId} />
        </dl>
      )}
      {!isB2B && (
        <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
          <Row label="Vorname" value={b.firstName} />
          <Row label="Nachname" value={b.lastName} />
          <Row label="Straße" value={b.street} colSpan={2} />
          <Row label="PLZ" value={b.zip} />
          <Row label="Stadt" value={b.city} />
          <Row label="Land" value={b.country} />
          <Row label="E-Mail" value={b.email} />
          <Row label="Telefon" value={b.phone} />
        </dl>
      )}
      <p className="text-ink-soft text-xs">
        {guest.rentalAgreementAcceptedAt ? (
          <>✓ Mietvertrag akzeptiert am {formatDateTime(guest.rentalAgreementAcceptedAt)}</>
        ) : (
          <span className="text-ink font-semibold">⚠ Mietvertrag noch nicht akzeptiert</span>
        )}
      </p>
    </>
  );
}

function Row({ label, value, colSpan }: { label: string; value: string | null; colSpan?: number }) {
  return (
    <div style={colSpan ? { gridColumn: `span ${colSpan}` } : undefined}>
      <dt className="text-ink-soft font-mono text-[10px] font-semibold uppercase tracking-[0.12em]">
        {label}
      </dt>
      {/*
        Auch der Leerstring ist „nicht gesetzt". Seit die Rechnungs-E-Mail optional ist, kommt er
        wirklich vor — und eine Beschriftung ohne Wert darunter sieht nach einem Fehler aus, nicht
        nach einem leer gelassenen Feld.
      */}
      <dd className="break-words">{value?.trim() ? value : '—'}</dd>
    </div>
  );
}
